import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { env } from "../../../env";
import axios from "axios";
import LoaderIcon from "../../Common/Icon/Loader";

const CustomerInfoModal = ({ customerId, setShowCustomerInfo }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showOperatingHour, setShowOperatingHour] = useState(false);
    const [showWorkingHour, setShowWorkingHour] = useState(false);
    const [workingHour, setWorkingHour] = useState('Working Hours');
    const [workingHourId, setWorkingHourId] = useState(1);
    const [customerData, setCustomerData] = useState({});
    const [isLoader, setIsLoader] = useState(true);
    const baseUrl = env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const formatter1 = new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    useEffect(() => {
        const fetchData = async () => {
            await getCustomerDataApi(baseUrl, token);
        };
        fetchData();
    }, [baseUrl, token]);

    const getCustomerDataApi = async (url, token) => {
        await axios.post(url+"/api/Customer/GetCustomerDetail", {customerid: customerId},{ 
            headers: {
                "Content-Type" : "multipart/form-data",
                "Authorization" : `Bearer ${token}`
            }
        }).then((response) => {
            const customer = response.data[0];
            setCustomerData(customer);
            setIsLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };

    const filteredHours = customerData.operating_hoursList
    ? customerData.operating_hoursList.filter(
            hour => hour.typeOfCalendarID === workingHourId
        )
    : [];

    // Group the filtered hours by dayOfTheWeek
    const groupedHours = filteredHours.reduce((acc, curr) => {
        const day = curr.dayOfTheWeek;
        if (!acc[day]) {
            acc[day] = [];
        }
        acc[day].push(curr);
        return acc;
    }, {});

    // Order days as desired (optional)
    const daysOrder = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
    ];
    const sortedDays = Object.keys(groupedHours).sort(
        (a, b) => daysOrder.indexOf(a) - daysOrder.indexOf(b)
    );

    // Calculate total weekly operating hours in minutes, then convert to hours.
    const totalMinutes = filteredHours.reduce((sum, item) => {
        const start = moment(item.fromTime);
        const end = moment(item.toTime);
        // Calculate difference in minutes
        const diff = end.diff(start, 'minutes');
        return sum + diff;
    }, 0);
    const totalHours = totalMinutes / 60;

    // functions
    const handleShowCustomerInfoModal = (e) => {
        e.preventDefault();
        setShowCustomerInfo()
    };
    const handleWorkingHour = (e, value) => {
        e.preventDefault();
        setWorkingHour(value.title);
        setWorkingHourId(value.id)
        setShowWorkingHour(!showWorkingHour);
    };

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-end sm:items-center z-50">
            <div className="bg-white rounded-t-lg sm:rounded-lg shadow-lg w-[28rem] p-2.5 sm:p-6">
                { isLoader
                    ?
                        <LoaderIcon />
                    :
                        <div className="space-y-2 sm:space-y-4">
                            <div className="flex justify-between items-center">
                                <div className='flex items-center space-x-2'>
                                    <h3 className="text-lg font-semibold capitalize">
                                        {customerData.name} ({customerData.id})
                                    </h3>
                                </div>
                                <button onClick={(e) => handleShowCustomerInfoModal(e)} className="text-gray-500 hover:text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                            <div className="overflow-x-auto min-h-44 max-h-[22rem] sm:max-h-96">
                                <div className="flex justify-between items-center p-2">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/cust-balance.svg" alt="balance" />
                                        <div className="text-light-700 capitalize">{ t("balance")}</div>
                                    </div>
                                    <div className="text-sm font-semibold">  
                                        {customerData.balance ? (formatter1.format(customerData.balance)+' €') : '--'}
                                    </div>
                                </div>
                                <div className="border-t-2 border-dashed border-background-normal"></div>
                                <div className="flex justify-between items-center p-2">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/money.svg" alt="turnover" />
                                        <div className="text-light-700 capitalize">{ t("previous turnover")}</div>
                                    </div>
                                    <div className="text-sm font-semibold">  
                                        {customerData.previousTurnover ? (formatter1.format(customerData.previousTurnover)+' €'): '--'}
                                    </div>
                                </div>
                                <div className="border-t-2 border-dashed border-background-normal"></div>
                                <div className="flex justify-between items-center p-2">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/money.svg" alt="turnover" />
                                        <div className="text-light-700 capitalize">{ t("turnover")}</div>
                                    </div>
                                    <div className="text-sm font-semibold">  
                                        {customerData.turnover ? (formatter1.format(customerData.turnover)+' €') : '--'}
                                    </div>
                                </div>
                                <div className="border-t-2 border-dashed border-background-normal"></div>
                                <div className="flex justify-between items-center p-2">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/calendar.svg" alt="calendar" />
                                        <div className="text-light-700 capitalize">{ t("last order date")}</div>
                                    </div>
                                    <div className="text-sm font-semibold">  
                                        {customerData.lastOrderDate ? moment(customerData.lastOrderDate).format('LL') : '--'}
                                    </div>
                                </div>
                                <div className="border-t-2 border-dashed border-background-normal"></div>
                                <div className="flex justify-between items-center p-2">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/wallet.svg" alt="wallet" />
                                        <div className="text-light-700 capitalize">{ t("last payment date")}</div>
                                    </div>
                                    <div className="text-sm font-semibold">  
                                        --
                                    </div>
                                </div>
                                <div className="border-t-2 border-dashed border-background-normal"></div>
                                <div className="flex justify-between items-center p-2 bg-blue-light1">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/calendar.svg" alt="calendar" />
                                        <div className="text-light-700 capitalize">{ t("last visit date")}</div>
                                    </div>
                                    <div className="text-sm font-semibold"> 
                                        {customerData.lastVisitedDate ? moment(customerData.lastVisitedDate).format('LL') : '--'}
                                    </div>
                                </div>
                                <div className="border-t-2 border-dashed border-background-normal"></div>
                                <div className="flex justify-between items-center p-2 bg-blue-light1">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/calendar.svg" alt="calendar" />
                                        <div className="text-light-700 capitalize">{ t("operating periods")}</div>
                                    </div>
                                    <div className="text-sm font-semibold">  
                                        {customerData.operating_periodsList && customerData.operating_periodsList.length > 0
                                            ? customerData.operating_periodsList
                                                .map(period => `${moment(period.fromDate).format('MMM')} - ${moment(period.toDate).format('MMM')}`)
                                                .join(', ')
                                            : '--'}
                                    </div>
                                </div>
                                <div className="border-t-2 border-dashed border-background-normal"></div>
                                <div className="flex justify-between items-center p-2 bg-yellow-light1">
                                    <div className="flex text-sm items-center space-x-2">
                                        <img src="../../assets/icons/profile/icon/calendar.svg" alt="calendar" />
                                        <div className="text-light-700 capitalize">{ t("next recommended day")}</div>
                                    </div>
                                    <div className="text-sm font-semibold">  
                                        --
                                    </div>
                                </div>
                                <div className="border-t-2 border-background-normal"></div>
                                <div onClick={() => setShowOperatingHour(!showOperatingHour)} className="flex justify-between items-center p-2 cursor-pointer">
                                    <div className="text-sm font-semibold capitalize">{ t("operating hours")}</div>
                                    <div className="text-sm font-semibold">  
                                        <img src={`../../assets/icons/select-${showOperatingHour ? 'up' : 'down'}.svg`} alt="arrow" />
                                    </div>
                                </div>
                                {showOperatingHour
                                    ? 
                                        <div className="space-y-2">
                                            <div className="grid grid-cols-2 gap-2 space-x-2">
                                                <div className="relative">
                                                    <div onClick={(e) => setShowWorkingHour(!showWorkingHour)} className="py-2 px-2 flex items-center space-x-2 rounded-lg text-black bg-gray-dark3 cursor-pointer">
                                                        <img src="../../assets/icons/profile/icon/hour.svg" alt="hour" />
                                                        <span className="capitalize font-medium text-sm">{workingHour}</span>
                                                        <img src="../../assets/icons/profile/icon/down-arrow.svg" alt="arrow" />
                                                    </div>
                                                    {
                                                        showWorkingHour
                                                        ? 
                                                            <ul className="absolute z-30 bg-white shadow-md top-10 right-0 rounded-md w-full text-xs">
                                                                {customerData.typeOfCalendarList &&
                                                                    customerData.typeOfCalendarList.map((hours, index) => (
                                                                        <li key={`type${index}`} onClick={(e) => handleWorkingHour((e), hours)}  className="bg-white hover:bg-action-hover py-2 px-4 block whitespace-no-wrap cursor-pointer capitalize">{hours.title}</li>
                                                                    ))
                                                                }
                                                            </ul>  
                                                        : null
                                                    }
                                                </div>
                                                <div className="rounded-lg py-2 px-2 border border-background-normal text-center text-black text-sm">{totalHours > 0 ? `${totalHours} hour/week` : '0 hour/week'}</div>
                                            </div>
                                            <div>
                                                {filteredHours.length > 0 
                                                ? 
                                                    <table className="w-full text-xs">
                                                        <thead className="text-gray-normal border border-background-normal">
                                                            <tr>
                                                                <th scope="col" className="border-r border-background-normal font-normal py-4 px-4">
                                                                    <div className="flex">
                                                                        <div className="mr-2 uppercase">
                                                                            { t('day')}
                                                                        </div>
                                                                    </div>
                                                                </th>
                                                                <th scope="col" className="font-normal py-4 px-4">
                                                                    <div className="flex">
                                                                        <div className="mr-2 uppercase">
                                                                            { t('time')}
                                                                        </div>
                                                                    </div>
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sortedDays.length > 0 ? (
                                                                sortedDays.map((day, index) => (
                                                                    <tr key={`day${index}`}>
                                                                        <td className="py-4 px-4 border border-background-normal">
                                                                            {day}
                                                                        </td>
                                                                        <td className="py-4 px-4 border border-background-normal">
                                                                            {groupedHours[day].map((item, index) => (
                                                                                <div key={`workinghours${index}`}>{moment(item.fromTime).format('h:mm A')} - {moment(item.toTime).format('h:mm A')}</div>
                                                                            ))}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="2" className="py-4 px-4 border border-background-normal">
                                                                        --
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                :
                                                    <div className="text-center py-4 font-medium uppercase">{ t('no records available')}</div>
                                                }
                                            </div>
                                        </div>
                                    :
                                        <div className="border-t-2 border-background-normal"></div>
                                }
                            </div>
                            <div className="flex justify-between items-center text-sm sm:text-base">
                                <button onClick={(e) => handleShowCustomerInfoModal(e)} className="px-4 p-2 bg-white text-gray-dark4 capitalize font-medium border-2 border-background-normal rounded-lg">
                                    { t('close')}
                                </button>
                                <button onClick={(e) => navigate(`/profiles/detail/${8337}`)} className="px-4 p-2 bg-blue-normal text-white rounded-lg capitalize">
                                    { t('go to customer profile')}
                                </button>
                            </div>
                        </div>
                }
            </div>
        </div>
    );
};

export default CustomerInfoModal;
